/**
 * Desktop Services Initializer
 *
 * Called once at app startup from main.tsx.
 * Only performs work in Tauri (desktop) environment.
 *
 * Responsibilities:
 * 1. Initialize SQLite database + run migrations
 * 2. Start connectivity monitor (online/offline detection)
 * 3. Start sync engine (processes queued offline actions)
 * 4. Start auto-update checker (delayed check on launch)
 */

import { isTauri } from './database';
import { initConnectivityMonitor } from './dataLayer';

let _initialized = false;

/**
 * Initialize desktop-specific services.
 * Safe to call in web — immediately returns.
 * Safe to call multiple times — only runs once.
 */
export async function initDesktopServices(): Promise<void> {
  if (_initialized) return;
  _initialized = true;

  // Always start connectivity monitoring (works in both web and desktop)
  initConnectivityMonitor();

  // SQLite + sync engine + auto-update only in Tauri
  if (!isTauri()) return;

  try {
    const { initDatabase } = await import('./database');
    await initDatabase();
    console.log('[pyxl] SQLite database initialized');

    // Start sync engine after database is ready
    const { initSyncEngine } = await import('./syncEngine');
    initSyncEngine();
    console.log('[pyxl] Sync engine started');
  } catch (error) {
    console.error('[pyxl] Failed to initialize desktop services:', error);
    // Non-fatal: the app still works, just without local persistence.
    // The data layer will fall through to Firestore-only paths.
  }

  // Auto-update checker (independent of database)
  try {
    const { initAutoUpdate } = await import('../updater');
    initAutoUpdate();
    console.log('[pyxl] Auto-update checker started');
  } catch (error) {
    console.error('[pyxl] Failed to initialize auto-update:', error);
    // Non-fatal: app works fine without update checking
  }
}
